<template>
  <div
    class="tw-min-h-screen tw-w-full tw-fixed tw-top-0 tw-left-0 tw-z-20 tw-bg-white dark:tw-bg-dm"
  >
    <div
      v-if="foreigner"
      class="tw-flex tw-h-full"
    >
      <aside class="tw-relative tw-w-80 tw-border-r tw-border-gray dark:tw-border-dm-gray tw-flex-shrink-0">
        <header class="tw-h-16 tw-flex tw-p-3 tw-border-b tw-border-gray dark:tw-border-dm-gray">
          <ForeignerFamilyPopoverList
            :family="foreigner?.family_profiles?.map((item) => ({
              avatar_url: item.avatar_url,
              relation_with_id: item.id,
              relation_with_fullname: item.fullname,
              relation_type: item.relation_type_name,
              relation_type_name: $store.getters['v2/shared/enums/get']('family_relations', item.relation_type) ? $store.getters['v2/shared/enums/get']('family_relations', item.relation_type).name : '',
            }))"
            :foreigner-id="foreigner?.id"
            show-messages-count
            class="tw-mr-2.5 tw-leading-[0px]"
            store-path="v2/foreigner/messaging"
            :redirect-on-family-member-click="false"
            @family-member-click="handleFamilyMemberChange"
          >
            <i-user-avatar
              :avatar="foreigner?.avatar_url && foreigner?.avatar_url['128'] && `${$baseHost}/${foreigner?.avatar_url['128']}`"
              :fullname="`${foreigner?.first_name} ${foreigner?.last_name}`"
              size="mini"
            />
          </ForeignerFamilyPopoverList>
          <ForeignerMessagingFulltextSearch />
        </header>
        <div
          ref="ForeignerMessagingThreads"
          class="ForeignerMessagingThreads tw-overflow-auto"
          :class="openedThreadData.id === 'all' || fulltextSearchValue.length ? 'tw-divide-y tw-divide-gray dark:tw-divide-dm-gray tw-px-2' : 'tw-px-2'"
          @scroll="handleScroll"
        >
          <template v-if="fulltextSearchValue.length">
            <ForeignerMessagingThread
              v-for="message in $store.state.v2.foreigner.messaging.fulltextSearchResults"
              :key="message.id"
              :thread="message"
              :is-active="message.id === $store.state.v2.foreigner.messaging.fulltextMessageId"
              @click.native="() => {
                $store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: String(message.permit_id), foreignerId: message.foreigner_id}]);
                $store.commit('v2/foreigner/messaging/set', ['fulltextMessageId', message.id]);
              }"
            />
          </template>

          <template v-else>
            <div
              v-if="threads.length === 0"
              class="tw-flex tw-h-full tw-justify-center tw-items-center"
            >
              {{ $t('messaging.no_threads') }}
            </div>
            <ForeignerMessagingThread
              v-for="thread in threads"
              :key="thread.id"
              :thread="thread"
              :is-active="String(thread.permit_id) === String(openedThreadData.id)"
              @click.native="() => {
                $store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: String(thread.permit_id), foreignerId: thread.foreigner_id }]);
                $route.params.mid && $router.replace({ params: { pid: String(thread.permit_id), mid: null } });
              }"
            />
          </template>
          <div
            v-if="moreThreadsUrl"
            v-loading="true"
            class="tw-w-full tw-h-14"
          />
        </div>
        <i-button
          v-if="showAddThreadButton"
          class="tw-fixed tw-bottom-[74px] tw-left-[256px] tw-shadow tw-border-none"
          is-icon
          type="white"
          @click="isNewThreadDialogOpen = true"
        >
          <icon-plus />
        </i-button>
        <div class="tw-m-3">
          <i-button
            class="tw-w-full"
            @click="() => {
              $store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: 'all', foreignerId: null }]);
              $store.commit('v2/foreigner/messaging/set', ['fulltextSearchValue', '']);
              $store.commit('v2/foreigner/messaging/set', ['fulltextMessageId', null]);
            }"
          >
            <span class="tw-font-bold">
              {{ $t('messaging.all_threads') }}
            </span>
          </i-button>
        </div>
      </aside>
      <div class="tw-flex-1">
        <header class="tw-h-16 tw-border-b tw-border-gray dark:tw-border-dm-gray tw-relative">
          <ForeignerMessagingFilterSearch
            v-if="isSearchOpen"
            class="tw-px-8 tw-gap-2"
            show-close
            @close="isSearchOpen = false"
          />
          <div
            v-else-if="isFiltersOpen"
            class="tw-flex tw-h-full tw-justify-center tw-items-center tw-px-8"
          >
            <ForeignerMessagingFilters
              class="tw-flex-1"
              is-inline
            />
            <i-button
              size="medium"
              is-icon
              class="tw-ml-6"
              @click="() => {
                isFiltersOpen = false;
                $store.commit('v2/foreigner/messaging/updateFilter', { field: 'user_from', payload: undefined });
                $store.commit('v2/foreigner/messaging/updateFilter', { field: 'permit', payload: undefined });
                $store.commit('v2/foreigner/messaging/updateFilter', { field: 'date_create_from', payload: undefined });
                $store.commit('v2/foreigner/messaging/updateFilter', { field: 'date_create_to', payload: undefined });
              }"
            >
              <icon-close />
            </i-button>
          </div>
          <div
            v-else
            class="tw-flex tw-h-full tw-justify-center tw-items-center"
          >
            <div class="tw-font-bold">
              {{ permit ? permit.subject : $t('messaging.all_threads') }}
            </div>
            <div class="tw-absolute tw-top-3 tw-right-8 tw-flex tw-gap-3 tw-items-center">
              <i-button
                size="medium"
                is-icon
                @click="isSearchOpen = true"
              >
                <icon-search />
              </i-button>
              <i-button
                size="medium"
                is-icon
                @click="isFiltersOpen = true"
              >
                <icon-filters />
              </i-button>
              <router-link
                v-if="foreigner"
                :to="isMe ? '/' : `/foreigner/${foreigner?.id}`"
              >
                <i-button
                  size="medium"
                  is-icon
                >
                  <icon-close />
                </i-button>
              </router-link>
            </div>
          </div>
        </header>
        <div class="ForeignerMessagingMessages tw-bg-light dark:tw-bg-transparent tw-bg-opacity-40">
          <ForeignerMessagingMessages
            v-if="openedThreadData.id"
            class="tw-max-w-3xl tw-mx-auto"
            :show-permit="openedThreadData.id === 'all'"
            :show-send-message="openedThreadData.id !== 'all'"
            is-fullscreen
          />
        </div>
      </div>
    </div>

    <ForeignersNewThreadDialog
      v-if="foreigner"
      :is-open="isNewThreadDialogOpen"
      :can-be-opened-threads="canBeOpenedThreads"
      store-path="v2/foreigner/messaging"
      @close="isNewThreadDialogOpen = false"
    />
  </div>
</template>

<script>
import ForeignerFamilyPopoverList from '@/components/Foreigner/ForeignerFamilyPopoverList.vue';
import ForeignersNewThreadDialog from '@/components/Foreigners/Messaging/ForeignersNewThreadDialog.vue';
import { mapGetters, mapState } from 'vuex';
import ForeignerMessagingMessages from '@/components/Foreigner/Messaging/ForeignerMessagingMessages.vue';
import ForeignerMessagingThread from '@/components/Foreigner/Messaging/ForeignerMessagingThread.vue';
import ForeignerMessagingFilterSearch from '@/components/Foreigner/Messaging/ForeignerMessagingFilterSearch.vue';
import ForeignerMessagingFilters from '@/components/Foreigner/Messaging/ForeignerMessagingFilters.vue';
import ForeignerMessagingFulltextSearch from '@/components/Foreigner/Messaging/ForeignerMessagingFulltextSearch.vue';

export default {
  name: 'Messaging',
  components: {
    ForeignerMessagingFulltextSearch,
    ForeignerMessagingFilters,
    ForeignerMessagingFilterSearch,
    ForeignerMessagingThread,
    ForeignerMessagingMessages,
    ForeignersNewThreadDialog,
    ForeignerFamilyPopoverList,
  },

  data() {
    return {
      isSearchOpen: false,
      isFiltersOpen: false,
      timerInterval: null,
      isNewThreadDialogOpen: false,
      isMoreThreadsLoading: false,
    };
  },

  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      foreigner: (state) => state.v2.foreigner.messaging.foreigner,
      openedThreadData: (state) => state.v2.foreigner.messaging.openedThreadData,
      moreThreadsUrl: (state) => state.v2.foreigner.messaging.moreThreadsUrl,
      fulltextSearchValue: (state) => state.v2.foreigner.messaging.fulltextSearchValue,
    }),
    ...mapGetters({
      threads: 'v2/foreigner/messaging/threads',
      openedThread: 'v2/foreigner/messaging/getOpenedThreadPermit',
      canBeOpenedThreads: 'v2/foreigner/messaging/canBeOpenedThreads',
    }),
    permit() {
      return this.threads.find(({ permit_id }) => String(permit_id) === String(this.openedThreadData.id));
    },
    isMe() {
      return this.$route.params.fid == null;
    },
    showAddThreadButton() {
      return this.foreigner && this.canBeOpenedThreads.length !== 0;
    },
  },

  watch: {
    'openedThread.foreigner_id'(newThreadForeignerId) {
      if (newThreadForeignerId) {
        this.$store.dispatch('v2/foreigner/messaging/loadMessagingUsers', { foreignerId: newThreadForeignerId });
      }
    },
  },

  async mounted() {
    this.$store.commit('v2/foreigner/messaging/set', ['foreigner', null]);
    const isFamilyMessaging = this.$route.name === 'FamilyMessaging' || this.$route.name === 'FamilyMessagingPID' || this.$route.name === 'FamilyMessagingMID';
    const foreignerId = isFamilyMessaging ? this.$route.params.fid : this.me.id;
    this.$store.dispatch('v2/foreigner/messaging/loadThreads', foreignerId);
    this.$store.dispatch('v2/foreigner/messaging/loadForeignerDetail', foreignerId);
    this.$store.dispatch('v2/foreigner/messaging/loadForeignerPermits', foreignerId);
    this.$store.dispatch('v2/foreigner/messaging/loadMessagingUsers', { foreignerId });
    this.$store.dispatch('v2/foreigner/permits/load', foreignerId);

    if (this.$route.params.pid) {
      this.$store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: this.$route.params.pid, foreignerId: this.$route.params.fid }]);
    } else if (!this.openedThreadData.id) {
      if (this.$route.params.fid !== 'preview') {
        this.$store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: 'all', foreignerId: this.$route.params.fid }]);
      } else {
        this.$store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: null, foreignerId: null }]);
      }
    }

    this.timerInterval = setInterval(() => {
      this.loadThreads();
    }, 30 * 1000);
  },
  beforeUnmount() {
    clearTimeout(this.timerInterval);
  },

  methods: {
    async loadThreads() {
      await this.$store.dispatch('v2/foreigner/messaging/loadThreads', this.foreigner?.id ?? this.me?.id);
    },
    async loadMoreThreads() {
      this.isMoreThreadsLoading = true;
      await this.$store.dispatch('v2/foreigner/messaging/loadMoreThreads');
      this.isMoreThreadsLoading = false;
    },
    handleScroll() {
      const container = this.$refs.ForeignerMessagingThreads;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight; // Adjust for visible height

      if (this.moreThreadsUrl && !this.isMoreThreadsLoading && distanceToBottom < 10) {
        this.loadMoreThreads();
      }
    },
    async handleFamilyMemberChange(foreignerId) {
      if (foreignerId) {
        await this.$store.dispatch('v2/foreigner/messaging/loadForeignerDetail', foreignerId);
        this.$store.dispatch('v2/foreigner/messaging/loadForeignerPermits', foreignerId);
      }
      await this.loadThreads();
      this.$store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: 'all', foreignerId: this.foreigner.id }]);
    },
  },
};
</script>

<style lang="scss" scoped>
.ForeignerMessagingThreads {
  height: calc(100vh - 4rem - 4.5rem);
  overscroll-behavior: none;

  &.full-height {
    height: calc(100vh - 4rem);
  }
}

.ForeignerMessagingMessages.full-screen {
  height: calc(100vh - 4rem);
}
</style>
